import React, { useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import DetailsCard from '../../../ui/layout/DetailsCard';
import ActionButton from '../../../ui/primitives/ActionButton';
import Loader from '../../../ui/primitives/Loader';
import { getRepositoryRedundancyInsights } from '../../../modules/dashboard';
import QUERY_KEYS from '../../../query/queryKeys';
import RedundancyDatasetModal from './RedundancyDatasetModal';

const METRIC_CONFIG = [
  {
    key: 'duplicates',
    label: 'Duplicate / Similar Test Case Clusters',
    icon: ContentCopyOutlinedIcon,
    dataset: 'similar',
    insightKey: 'potential_duplicate_similar_test_cases',
    getValue: (m) => m.potential_duplicate_similar_test_cases,
  },
  {
    key: 'avgExecutions',
    label: 'Average Executions / Test Case',
    icon: RepeatOutlinedIcon,
    insightKey: 'average_executions_per_test_case',
    getValue: (m) => m.average_executions_per_test_case,
  },
  {
    key: 'frequentManual',
    label: 'Frequently Executed Manual Tests',
    icon: PlaylistAddCheckOutlinedIcon,
    dataset: 'frequent-manual',
    insightKey: 'frequently_executed_manual_tests',
    getValue: (m) => m.frequently_executed_manual_tests,
  },
  {
    key: 'rarelyUsed',
    label: 'Rarely Used Tests',
    icon: HourglassEmptyOutlinedIcon,
    dataset: 'rarely-used',
    insightKey: 'rarely_used_test_cases',
    getValue: (m) => m.rarely_used_test_cases,
    getPercentage: (m) => m.rarely_used_percentage,
  },
];

const RedundancyEfficiencyCard = ({ sx = {} }) => {
  const { projectId } = useParams();
  const [activeMetric, setActiveMetric] = useState(null);
  const [hoveredInsightKey, setHoveredInsightKey] = useState(METRIC_CONFIG[0].insightKey);

  const {
    data,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEYS.project.redundancyInsights(projectId),
    queryFn: () => getRepositoryRedundancyInsights(projectId),
    enabled: !!projectId,
  });

  const metrics = data?.metrics || {};
  const insights = data?.insights || {};
  const activeInsightMessage = insights[hoveredInsightKey];

  const handleMetricClick = (config) => {
    if (!config.dataset) return;
    setActiveMetric({ dataset: config.dataset, title: config.label });
  };

  if (loading) {
    return (
      <DetailsCard title="Redundancy & Repository Efficiency" sx={sx}>
        <Loader minHeight={260} />
      </DetailsCard>
    );
  }

  if (isError) {
    return (
      <DetailsCard title="Redundancy & Repository Efficiency" sx={sx}>
        <Alert severity="error" action={<ActionButton label="Retry" onClick={() => refetch()} />}>
          {error?.message || 'Failed to load redundancy insights.'}
        </Alert>
      </DetailsCard>
    );
  }

  return (
    <DetailsCard title="Redundancy & Repository Efficiency" sx={sx}>
      <Stack spacing={1}>
        {METRIC_CONFIG.map((config) => {
          const Icon = config.icon;
          const value = config.getValue(metrics);
          const percentage = config.getPercentage?.(metrics);
          const clickable = Boolean(config.dataset);
          const isActive = hoveredInsightKey === config.insightKey;

          return (
            <Stack
              key={config.key}
              direction="row"
              spacing={1.5}
              alignItems="center"
              onMouseEnter={() => setHoveredInsightKey(config.insightKey)}
              sx={(theme) => ({
                py: 1,
                px: 1.5,
                borderRadius: `${theme.shape.borderRadius}px`,
                bgcolor: isActive ? alpha(theme.palette.info.main, 0.06) : 'transparent',
                transition: 'background-color 0.15s ease',
              })}
            >
              <Box
                sx={(theme) => ({
                  width: 36,
                  height: 36,
                  borderRadius: `${theme.shape.borderRadius}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  '& svg': { fontSize: 18 },
                })}
              >
                <Icon />
              </Box>

              <Typography
                variant="body2"
                color={clickable ? 'primary.main' : 'text.primary'}
                fontWeight={clickable ? 600 : 400}
                onClick={clickable ? () => handleMetricClick(config) : undefined}
                sx={{
                  width: 'fit-content',
                  maxWidth: '100%',
                  cursor: clickable ? 'pointer' : 'default',
                }}
              >
                {config.label}
                {config.subtitle ? ` (${config.subtitle})` : ''}
              </Typography>
              <Box sx={{ flex: 1 }} />

              <Typography variant="subtitle1" fontWeight={700} whiteSpace="nowrap">
                {value ?? '--'}
                {/* {percentage !== undefined && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    ({percentage}%)
                </Typography>
                )} */}
              </Typography>
            </Stack>
          );
        })}

        {activeInsightMessage && (
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: (t) => `${t.shape.borderRadius}px`,
              bgcolor: (t) => alpha(t.palette.info.main, 0.06),
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.info.main, 0.2),
            }}
          >
            <Typography variant="caption" fontWeight={700} color="info.dark" display="block" sx={{ mb: 0.25 }}>
              AI Insight
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeInsightMessage}
            </Typography>
          </Box>
        )}
      </Stack>

      <RedundancyDatasetModal
        open={Boolean(activeMetric)}
        onClose={() => setActiveMetric(null)}
        dataset={activeMetric?.dataset}
        title={activeMetric?.title}
        projectId={projectId}
      />
    </DetailsCard>
  );
};

export default RedundancyEfficiencyCard;